import React, { useState } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Chip,
  Menu,
  MenuItem,
  InputAdornment,
  Tabs,
  Tab,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import DataTable from '../../components/tables/DataTable';

const BankingList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selected, setSelected] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  const transactions = [
    {
      id: 'TXN-001',
      date: '2024-06-15',
      type: 'Credit',
      description: 'Payment from Customer A',
      amount: 45000,
      balance: 125000,
      account: 'HDFC Bank - Current',
      category: 'Sales',
    },
    {
      id: 'TXN-002',
      date: '2024-06-14',
      type: 'Debit',
      description: 'Payment to Vendor B',
      amount: 25000,
      balance: 80000,
      account: 'HDFC Bank - Current',
      category: 'Purchases',
    },
    {
      id: 'TXN-003',
      date: '2024-06-13',
      type: 'Credit',
      description: 'Payment from Customer C',
      amount: 38000,
      balance: 105000,
      account: 'ICICI Bank - Savings',
      category: 'Sales',
    },
    {
      id: 'TXN-004',
      date: '2024-06-12',
      type: 'Debit',
      description: 'Office Rent',
      amount: 15000,
      balance: 67000,
      account: 'HDFC Bank - Current',
      category: 'Expense',
    },
    {
      id: 'TXN-005',
      date: '2024-06-10',
      type: 'Credit',
      description: 'Cash Deposit',
      amount: 20000,
      balance: 82000,
      account: 'HDFC Bank - Current',
      category: 'Cash',
    },
  ];

  const columns = [
    { id: 'date', label: 'Date', sortable: true },
    { id: 'id', label: 'Transaction ID', sortable: true },
    {
      id: 'type',
      label: 'Type',
      sortable: true,
      render: (value) => (
        <Chip
          label={value}
          size="small"
          sx={{
            backgroundColor: value === 'Credit' ? '#10B98115' : '#EF444415',
            color: value === 'Credit' ? '#10B981' : '#EF4444',
            fontWeight: 500,
          }}
        />
      ),
    },
    { id: 'description', label: 'Description', sortable: true },
    {
      id: 'amount',
      label: 'Amount',
      sortable: true,
      render: (value, row) => (
        <Typography sx={{ fontWeight: 600, color: row.type === 'Credit' ? '#10B981' : '#EF4444' }}>
          {row.type === 'Credit' ? '+' : '-'}{value.toLocaleString()}
        </Typography>
      ),
    },
    {
      id: 'balance',
      label: 'Balance',
      sortable: true,
      render: (value) => `${value.toLocaleString()}`,
    },
    { id: 'account', label: 'Account', sortable: true },
    { id: 'category', label: 'Category', sortable: true },
  ];

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = transactions.map((n) => n.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, row) => {
    const selectedIndex = selected.indexOf(row.id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, row.id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleSort = (columnId, direction) => {
    setSortColumn(columnId);
    setSortDirection(direction);
  };

  const handleFilterClick = (event) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const filteredTransactions = transactions.filter((txn) =>
    txn.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    txn.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, color: '#1E293B', mb: 1 }}>
          Banking
        </Typography>
        <Typography variant="body1" sx={{ color: '#64748B', fontSize: '0.875rem' }}>
          Manage bank accounts and transactions
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                border: '1px solid #E2E8F0',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', mb: 1 }}>
                  Total Balance
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, color: '#1E293B' }}>
                  3,50,000
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                border: '1px solid #E2E8F0',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', mb: 1 }}>
                  Bank Accounts
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, color: '#2563EB' }}>
                  3
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                border: '1px solid #E2E8F0',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', mb: 1 }}>
                  Cash Balance
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, color: '#10B981' }}>
                  25,000
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
                border: '1px solid #E2E8F0',
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.875rem', mb: 1 }}>
                  This Month
                </Typography>
                <Typography variant="h4" sx={{ fontSize: '2rem', fontWeight: 600, color: '#F59E0B' }}>
                  1,03,000
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Action Bar */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #E2E8F0',
          mb: 3,
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#64748B' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={handleFilterClick}
                  sx={{ borderRadius: 2 }}
                >
                  Filter
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  sx={{ borderRadius: 2 }}
                >
                  Export
                </Button>
                <IconButton>
                  <RefreshIcon />
                </IconButton>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  sx={{
                    backgroundColor: '#2563EB',
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: '#1E40AF',
                    },
                  }}
                >
                  New Transaction
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={handleFilterClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            borderRadius: 2,
          },
        }}
      >
        <MenuItem onClick={handleFilterClose}>All Transactions</MenuItem>
        <MenuItem onClick={handleFilterClose}>Credits</MenuItem>
        <MenuItem onClick={handleFilterClose}>Debits</MenuItem>
        <MenuItem onClick={handleFilterClose}>Sales</MenuItem>
        <MenuItem onClick={handleFilterClose}>Purchases</MenuItem>
        <MenuItem onClick={handleFilterClose}>Expenses</MenuItem>
      </Menu>

      {/* Tabs */}
      <Box sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Bank Accounts" />
          <Tab label="Transactions" />
          <Tab label="Cash" />
          <Tab label="Reconciliation" />
        </Tabs>
      </Box>

      {/* Data Table */}
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
          border: '1px solid #E2E8F0',
        }}
      >
        <DataTable
          columns={columns}
          data={filteredTransactions}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={setRowsPerPage}
          selectable
          selected={selected}
          onSelectAllClick={handleSelectAllClick}
          onClick={handleClick}
          onSort={handleSort}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
        />
      </Card>
    </Box>
  );
};

export default BankingList;
